// Sui: wallets through the Wallet Standard (Slush, Suiet, Phantom's Sui, …), and the open the api
// built (docs/builders-ui.md §3: `/build` returns `{ tx_bytes_base64 }`, `/submit` takes
// `<tx_bytes_base64>.<signature_base64>`).

import { Transaction } from "@mysten/sui/transactions";
import { getWallets, signTransaction, SUI_MAINNET_CHAIN, type Wallet, type WalletAccount } from "@mysten/wallet-standard";

export const SUI_CHAIN = SUI_MAINNET_CHAIN;

/** A wallet the demo can open with: it connects and signs Sui transactions (either generation). */
export function canOpenSui(w: Pick<Wallet, "features" | "chains">): boolean {
  const f = w.features as Record<string, unknown>;
  return !!f["standard:connect"] && !!(f["sui:signTransaction"] || f["sui:signTransactionBlock"]) && w.chains.some((c) => c === SUI_CHAIN);
}

/** Every Sui wallet registered in this page, now and as they register (returns the unsubscribe). */
export function discoverSuiWallets(onChange: (ws: Wallet[]) => void): () => void {
  const reg = getWallets();
  const emit = () => onChange(reg.get().filter(canOpenSui));
  emit();
  const offRegister = reg.on("register", emit);
  const offUnregister = reg.on("unregister", emit);
  return () => {
    offRegister();
    offUnregister();
  };
}

/** The wallet's first account on Sui mainnet, after `standard:connect`. */
export async function connectSui(w: Wallet): Promise<WalletAccount> {
  const connect = (w.features as any)["standard:connect"].connect as (i?: { silent?: boolean }) => Promise<{ accounts: readonly WalletAccount[] }>;
  const { accounts } = await connect();
  const account = (accounts.length ? accounts : w.accounts).find((a) => a.chains.some((c) => c === SUI_CHAIN));
  if (!account) throw new Error(`${w.name} has no Sui mainnet account`);
  return account;
}

/** Follow the wallet's account changes (`standard:events`): the first Sui mainnet account, or null
 *  when none is left. Returns the unsubscribe (a no-op for wallets without events). */
export function watchSuiAccount(w: Wallet, onChange: (account: WalletAccount | null) => void): () => void {
  const events = (w.features as any)["standard:events"] as { on: (e: "change", f: (p: { accounts?: readonly WalletAccount[] }) => void) => () => void } | undefined;
  if (!events) return () => {};
  return events.on("change", ({ accounts }) => {
    if (accounts) onChange(accounts.find((a) => a.chains.some((c) => c === SUI_CHAIN)) ?? null);
  });
}

/** What `/submit` takes for a Sui open: the bytes the wallet signed, a dot, its signature. */
export function suiSubmitPayload(signed: { bytes: string; signature: string }): string {
  if (!signed.bytes || !signed.signature || signed.bytes.includes(".") || signed.signature.includes(".")) throw new Error("the wallet returned no signed transaction");
  return `${signed.bytes}.${signed.signature}`;
}

/** Have the wallet sign the api's open as built: the transaction is handed over with its sender,
 *  gas and commands already set, so the wallet only signs it. Returns `/submit`'s payload. */
export async function signSuiOpen(w: Wallet, account: WalletAccount, txBytesBase64: string): Promise<string> {
  const json = await Transaction.from(txBytesBase64).toJSON();
  const signed = await signTransaction(w as any, { transaction: { toJSON: async () => json }, account, chain: SUI_CHAIN });
  return suiSubmitPayload(signed);
}

/** A Sui coin type with its address in full (64 hex digits): `0x2::sui::SUI` and
 *  `0x000…002::sui::SUI` are the same coin. */
export function normalizeSuiCoin(coin: string): string {
  const i = coin.indexOf("::");
  if (i < 0) return coin;
  const addr = coin.slice(0, i).replace(/^0x/i, "").toLowerCase().padStart(64, "0");
  return `0x${addr}${coin.slice(i)}`;
}

/** A Sui coin type from its gum-wire form (`0x03 ‖ len ‖ 32-byte address ‖ "::module::Struct"`). */
export function suiCoinFromWire(wire: string): string | null {
  const h = wire.replace(/^0x/, "").toLowerCase();
  if (!h.startsWith("03") || h.length < 4) return null;
  const len = parseInt(h.slice(2, 4), 16);
  const payload = h.slice(4);
  if (payload.length !== 2 * len || len < 34) return null;
  const rest = String.fromCharCode(...(payload.slice(64).match(/../g) ?? []).map((b) => parseInt(b, 16)));
  if (!rest.startsWith("::")) return null;
  return `0x${payload.slice(0, 64)}${rest}`;
}
